import { Typography } from "@mui/material"
import { DataGrid, GridColDef } from "@mui/x-data-grid"
import { translate } from "app/language/service"
import { getFormatedNumber } from "app/tools/number"
import { CURRENCIES } from "currency/model"
import { getValueInUserCurrency } from "currency/tools/common"
import { GeoTargetType } from "form/components/content/geo-targets/GeoTargetType"
import { getCurrencySymbol } from "form/inventory/tools/common"
import { GEO_TARGETS_TYPES, IReportShapeRow, ISummaryPageInfo } from "form/model"
import { UnexpectedErrorPage } from "page/components/UnexpectedErrorPage"
import React from "react"
import { getShapesReportRows } from "reports/tools/summary-reports"
import { SHAPE_TYPES } from "shape/model"
import { sortShapeType } from "shape/tools/common"
import { ColumnHeader } from "ui/components/ColumnHeader"
import { FlexBox } from "ui/components/FlexBox"
import { LinearLoader } from "ui/components/LinearLoader"
import { ListIdCell } from "ui/components/ListIdCell"
import { getDataGridTexts } from "ui/tools/list"

export const ReportShapes = React.memo(({ form }: ISummaryPageInfo): JSX.Element => {
  const [rows, setRows] = React.useState<IReportShapeRow[] | null>(null)
  const [error, setError] = React.useState("")
  React.useEffect(() => {
    if (rows !== null || error) {
      return
    }
    getShapesReportRows(form.campaignId).then(result => {
      if (typeof result === "string") {
        setError(result)
        return
      }
      setRows(result)
    })
  })
  if (error) {
    return <UnexpectedErrorPage message={error} />
  }
  if (rows === null) {
    return <LinearLoader text={translate("form.report.loading")} />
  }
  const columns: Array<GridColDef<IReportShapeRow>> = [
    {
      field: "type",
      headerName: translate("label.type"),
      flex: 1,
      minWidth: 150,
      sortComparator: (_1, _2, cell1, cell2) => sortShapeType(cell1.value as SHAPE_TYPES, cell2.value as SHAPE_TYPES),
      renderCell: params => <GeoTargetType type={params.row.type as GEO_TARGETS_TYPES} />
    },
    {
      field: "name",
      headerName: translate("label.name"),
      flex: 3
    },
    {
      field: "venueTypeName",
      headerName: translate("label.venue-type"),
      flex: 2,
      renderCell: params => <ListIdCell label={params.row.venueTypeName || "-"} id={params.row.venueTypeId} />
    },
    {
      field: "impressions_without_multiplier",
      renderHeader: () => (
        <ColumnHeader
          primaryText={translate("label.impressions")}
          secondaryText={translate("label.impression.no.multiplier")}
        />
      ),
      renderCell: params => getFormatedNumber(Number(params.row.impressions_without_multiplier.toFixed(2))),
      flex: 2
    },
    {
      field: "impressions_with_multiplier",
      renderHeader: () => (
        <ColumnHeader
          primaryText={translate("label.impressions")}
          secondaryText={translate("label.impression.with.multiplier")}
        />
      ),
      renderCell: params => getFormatedNumber(Number(params.row.impressions_with_multiplier.toFixed(2))),
      flex: 2
    },
    {
      field: "screens",
      headerName: translate("label.screens"),
      flex: 2,
      renderCell: params => getFormatedNumber(params.row.screens)
    },
    {
      field: "spend",
      headerName: `${translate("label.spend")} (${getCurrencySymbol()})`,
      flex: 2,
      renderCell: params => {
        const spend: null | number = params.row.spend
        if (!spend) {
          return ""
        }
        return (
          <Typography color="textPrimary" fontSize=".8rem">
            {getFormatedNumber(Number(getValueInUserCurrency(CURRENCIES.CAD, spend).toFixed(3)))}
          </Typography>
        )
      }
    },
    {
      field: "cpm",
      headerName: `${translate("label.cpm")} (${getCurrencySymbol()})`,
      flex: 1,
      minWidth: 120,
      renderCell: params => {
        const cpm: null | number = params.row.cpm
        if (!cpm) {
          return ""
        }
        return (
          <Typography color="textPrimary" fontSize=".8rem">
            {getFormatedNumber(Number(getValueInUserCurrency(CURRENCIES.CAD, cpm).toFixed(3)))}
          </Typography>
        )
      }
    }
  ]
  return (
    <FlexBox
      vertical
      ai="start"
      jc="start"
      sx={{
        "width": "100%",
        "p": "1rem",
        "pb": "1.5rem",
        "mb": "3rem",
        "animation": "fadeIn 600ms",
        "flexGrow": "1",
        "overflow": "auto",
        "& > *": {
          width: "100%"
        },
        "& .MuiDataGrid-selectedRowCount": {
          visibility: "hidden"
        },
        "& .MuiDataGrid-cell": {
          fontSize: ".8rem",
          fontWeight: 300
        },
        "& .MuiDataGrid-row": {
          cursor: "default"
        },
        "& .MuiDataGrid-row.Mui-selected": {
          backgroundColor: "transparent"
        },
        "& .targeted-check": {
          "color": theme => theme.palette.text.primary2,
          "& svg": {
            ml: "1rem"
          }
        },
        "& .MuiDataGrid-row:hover, & .MuiDataGrid-row.Mui-selected:hover": {
          backgroundColor: "transparent !important"
        },
        "& .icon": {
          color: theme => theme.palette.text.primary2
        },
        "& .icon.non-targeted": {
          color: theme => theme.palette.text.secondary
        },
        "& .MuiDataGrid-virtualScrollerRenderZone > div:nth-of-type(odd)": {
          backgroundColor: theme => theme.palette.background.default
        },
        "& .MuiDataGrid-virtualScrollerRenderZone > div:nth-of-type(odd):hover": {
          backgroundColor: "rgba(0, 0, 0, 0.04)"
        },
        "& .MuiDataGrid-columnHeader, & .MuiDataGrid-columnHeader:focus, & .MuiDataGrid-cell:focus, & .MuiDataGrid-cell":
          {
            outline: "none !important"
          }
      }}>
      <DataGrid
        rows={rows}
        columns={columns}
        autoHeight
        disableColumnFilter
        disableColumnMenu
        className="report-shapes-list"
        rowHeight={50}
        localeText={getDataGridTexts()}
        sx={{ mt: "1rem", height: "22rem", backgroundColor: theme => theme.palette.background.paper }}
      />
    </FlexBox>
  )
})
